import React from "react";
import { BillingPeriod, PricingPlan } from "../model/PricingModel";

interface ConfirmPlanChangeDialogProps {
  plan: PricingPlan;
  billingPeriod: BillingPeriod;
  action: "billingSwitch" | "upgrade" | "downgrade";
  currentPlanName?: string;
  currentBillingPeriod?: string;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const formatPrice = (price?: number) => {
  if (typeof price !== "number") return null;
  return `$${Number.isInteger(price) ? price.toFixed(0) : price.toFixed(2)}`;
};

const capitalize = (s?: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

const ConfirmPlanChangeDialog: React.FC<ConfirmPlanChangeDialogProps> = ({
  plan,
  billingPeriod,
  action,
  currentPlanName,
  currentBillingPeriod,
  isLoading,
  onConfirm,
  onCancel,
}) => {
  const isBillingSwitch = action === "billingSwitch";
  const isScheduledChange =
    action === "downgrade" || (isBillingSwitch && billingPeriod === "monthly");
  const isYearly = billingPeriod === "yearly";

  const price = isYearly
    ? plan.yearlyPriceDisplay || formatPrice(plan.priceYearly)
    : plan.monthlyPriceDisplay || formatPrice(plan.priceMonthly);
  const period = isYearly
    ? plan.yearlyPeriodDisplay || "/year"
    : plan.monthlyPeriodDisplay || "/month";
  const periodLabel = isYearly ? "Yearly" : "Monthly";

  const fromLabel = `${currentPlanName || ""} ${capitalize(
    currentBillingPeriod
  )}`.trim();
  const toLabel = `${plan.name} ${periodLabel}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onCancel}
      />

      {/* Dialog */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 z-10">
        {/* Icon */}
        <div
          className={`flex items-center justify-center w-14 h-14 rounded-full mx-auto mb-5 ${
            isScheduledChange ? "bg-amber-50" : "bg-blue-50"
          }`}
        >
          {isScheduledChange ? (
            <svg
              className="w-7 h-7 text-amber-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
              />
            </svg>
          ) : (
            <svg
              className="w-7 h-7 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
              />
            </svg>
          )}
        </div>

        <h2 className="text-xl font-bold text-gray-900 text-center mb-6">
          Confirm Plan Change
        </h2>

        {isScheduledChange ? (
          <>
            <p className="text-gray-700 text-center mb-6">
              You are about to change your subscription from{" "}
              <span className="font-semibold">{fromLabel}</span> to{" "}
              <span className="font-semibold">{toLabel}</span>.
            </p>

            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-8 text-sm text-amber-800">
              Your subscription will remain on{" "}
              <span className="font-semibold">'{fromLabel}'</span> for the
              remainder of the current billing period.{" "}
              <span className="font-semibold">'{toLabel}'</span> will become
              active when your subscription renews.
            </div>
          </>
        ) : (
          <>
            <p className="text-gray-700 text-center mb-6">
              You are about to switch to the{" "}
              <span className="font-semibold">{toLabel}</span> plan.
            </p>

            {price && (
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-5">
                <p className="text-xs text-blue-600 font-medium uppercase tracking-wide mb-1">
                  New billing amount
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {price}
                  <span className="text-base font-normal text-gray-500 ml-1">
                    {period}
                  </span>
                </p>
              </div>
            )}

            <p className="text-sm text-gray-500 text-center mb-8">
              Any amount already paid for your current subscription will be
              deducted from the new charge on a prorated basis.
            </p>
          </>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 py-3 px-4 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={`flex-1 py-3 px-4 rounded-xl text-white font-semibold text-sm transition-colors disabled:opacity-70 flex items-center justify-center gap-2 ${
              isScheduledChange
                ? "bg-amber-500 hover:bg-amber-600"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                  />
                </svg>
                Processing...
              </>
            ) : isScheduledChange ? (
              "Continue with the Downgrade"
            ) : isBillingSwitch ? (
              "Continue with the change"
            ) : (
              "Continue with the upgrade"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmPlanChangeDialog;
